/**
 * The course-section form, in one place (issues #95 and #485).
 *
 * Every field a section owns lived inside `CoursesPage.tsx`, private to the
 * create and edit dialogs. Issue #485 moved creation out of that page and into
 * the Courses list's guided create flow, which needs the SAME controls arranged into
 * its own numbered groups. Copying them would have produced two forms that
 * disagree about which fields the registrar owns, which options a term offers,
 * and what a cleared value sends — so the controls moved here instead, and both
 * surfaces compose the same atoms.
 *
 * The groups are exported individually because the guided flow interleaves them
 * with its own prerequisite context and helper column, while the edit dialog
 * still renders the three fieldsets it always had. `CourseFormFields` is that
 * composed arrangement, unchanged.
 */
import { useEffect, useState, type ComponentProps } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MEETING_PROVIDER_LABEL as PROVIDER_LABEL,
  MODALITY_LABEL,
  MODALITY_OPTIONS,
  type Modality,
} from "@/lib/api/courses";
import type { Program, Semester } from "@/lib/api/academicStructure";
import type { MeetingProvider } from "@/lib/api/tenant";
import {
  CourseMark,
  type CourseImageSettled,
} from "@/components/workspace/CourseMark";
import {
  COURSE_TIMEZONE_OPTIONS,
  courseTimezoneOptionLabel,
} from "@/lib/course-schedule";
import {
  courseImageUrlProblem,
  NONE,
  TIME_OPTIONS,
  WEEKDAY_OPTIONS,
  type CourseForm,
  type InstructorOption,
  type SetCourseField,
} from "@/lib/course-form";
import { isHistoricalTerm } from "@/lib/term-state";
import { cn } from "@/lib/utils";

interface FieldGroupProps {
  idPrefix: string;
  form: CourseForm;
  setField: SetCourseField;
  /**
   * The accepted course-write split: the registrar owns what a course IS
   * (code, title, credits, program, term, capacity, instructor of record,
   * and publication), and the assigned instructor owns how their section RUNS.
   * False renders that half as facts — `courses.py` 403s each of those fields
   * by name for a non-admin, so an editable control here would be a promise
   * the write path refuses.
   */
  canWriteRegistrarFields: boolean;
  /** A published section keeps its program, term, and instructor of record. */
  placementLocked?: boolean;
}

const fieldId = (idPrefix: string, suffix: string) => `${idPrefix}-${suffix}`;

/**
 * What an in-place editor adds to a typed control: leaving the field or Enter
 * saves it, Escape puts back the saved value, and a message under the field is
 * named here. The dialogs and the create flow pass none of it.
 */
export type CourseFieldCommit = Pick<
  ComponentProps<"input">,
  "onBlur" | "onKeyDown" | "aria-invalid" | "aria-describedby"
>;

/** One field of the section form, laid by its caller. */
interface FieldProps {
  idPrefix: string;
  form: CourseForm;
  setField: SetCourseField;
  className?: string;
}

/** A typed field: an in-place editor commits it, the dialogs only read it. */
interface TypedFieldProps extends FieldProps {
  commit?: CourseFieldCommit;
}

/**
 * One registrar-owned value, when the caller may not write it: named, never an
 * inert control. Follows the treatment the instructor-of-record field already
 * used, because "read the value and be told who owns it" is a better answer
 * than a disabled input that looks like it might become editable.
 */
export function RegistrarFact({ id, value }: { id: string; value: string }) {
  return (
    <p
      id={id}
      className="rounded-control border border-border bg-well px-3 py-2 text-ui text-muted-foreground"
    >
      {value || "—"}
      <span className="ml-2 text-meta">· set by an administrator</span>
    </p>
  );
}

export function CourseTermField({
  idPrefix, form, setField, canWriteRegistrarFields, placementLocked = false, semesters,
}: FieldGroupProps & { semesters: Semester[] }) {
  const id = fieldId(idPrefix, "semester");
  const selectedTerm = semesters.find((s) => s.id === form.semesterId);
  return (
    <div className="grid gap-2">
      <Label htmlFor={id}>Term</Label>
      {canWriteRegistrarFields ? (
        <Select value={form.semesterId} onValueChange={(v) => setField("semesterId", v)}>
          <SelectTrigger id={id} className="w-full"><SelectValue placeholder="Unassigned" /></SelectTrigger>
          <SelectContent>
            {!placementLocked && <SelectItem value={NONE}>Unassigned</SelectItem>}
            {semesters.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.name}{s.status === "closed" ? " · closed" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <RegistrarFact
          id={id}
          value={selectedTerm
            ? `${selectedTerm.name}${selectedTerm.status === "closed" ? " · closed" : ""}`
            : "Unassigned"}
        />
      )}
    </div>
  );
}

export function CourseProgramField({
  idPrefix, form, setField, canWriteRegistrarFields, placementLocked = false, programs,
}: FieldGroupProps & { programs: Program[] }) {
  const id = fieldId(idPrefix, "program");
  return (
    <div className="grid gap-2">
      <Label htmlFor={id}>Program</Label>
      {canWriteRegistrarFields ? (
        <Select value={form.programId} onValueChange={(v) => setField("programId", v)}>
          <SelectTrigger id={id} className="w-full"><SelectValue placeholder="Unassigned" /></SelectTrigger>
          <SelectContent>
            {!placementLocked && <SelectItem value={NONE}>Unassigned</SelectItem>}
            {programs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      ) : (
        <RegistrarFact
          id={id}
          value={programs.find((p) => p.id === form.programId)?.name ?? "Unassigned"}
        />
      )}
    </div>
  );
}

export function CourseInstructorField({
  idPrefix, form, setField, canWriteRegistrarFields, placementLocked = false, instructors,
  label = "Instructor of record",
}: FieldGroupProps & { instructors: InstructorOption[]; label?: string }) {
  const id = fieldId(idPrefix, "instructor");
  return (
    <div className="grid gap-2">
      <Label htmlFor={id}>{label}</Label>
      {canWriteRegistrarFields ? (
        <Select value={form.instructorFacultyProfileId} onValueChange={(v) => setField("instructorFacultyProfileId", v)}>
          <SelectTrigger id={id} className="w-full"><SelectValue placeholder="Unassigned" /></SelectTrigger>
          <SelectContent>
            {!placementLocked && <SelectItem value={NONE}>Unassigned</SelectItem>}
            {instructors.filter(i => !i.archived || i.faculty_profile_id === form.instructorFacultyProfileId).map((i) => (
              <SelectItem key={i.faculty_profile_id} value={i.faculty_profile_id} disabled={!i.eligible}>
                {i.label}{i.qualifier ? ` · ${i.qualifier}` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        // Named, not editable. Reassignment is an administrative act, and an
        // instructor who cleared their own section would lose access to it.
        <RegistrarFact
          id={id}
          value={instructors.find((i) => i.faculty_profile_id === form.instructorFacultyProfileId)?.label
            ?? "Unassigned"}
        />
      )}
    </div>
  );
}

export function CourseCodeField({
  idPrefix, form, setField, canWriteRegistrarFields, className, commit,
}: TypedFieldProps & { canWriteRegistrarFields: boolean }) {
  const id = fieldId(idPrefix, "code");
  return (
    <div className={cn(className, "grid gap-2")}>
      <Label htmlFor={id}>Code</Label>
      {canWriteRegistrarFields ? (
        <Input id={id} value={form.code} onChange={(e) => setField("code", e.target.value)} placeholder="THEO-201" {...commit} />
      ) : (
        <RegistrarFact id={id} value={form.code} />
      )}
    </div>
  );
}

export function CourseCreditsField({
  idPrefix, form, setField, canWriteRegistrarFields, className, commit,
}: TypedFieldProps & { canWriteRegistrarFields: boolean }) {
  const id = fieldId(idPrefix, "credits");
  return (
    <div className={cn(className, "grid gap-2")}>
      <Label htmlFor={id}>Credits</Label>
      {canWriteRegistrarFields ? (
        <Input
          id={id}
          type="number"
          min={0}
          value={form.credits}
          onChange={(e) => setField("credits", e.target.value)}
          {...commit}
        />
      ) : (
        <RegistrarFact id={id} value={form.credits} />
      )}
    </div>
  );
}

export function CourseTitleField({
  idPrefix, form, setField, canWriteRegistrarFields, className, commit,
}: TypedFieldProps & { canWriteRegistrarFields: boolean }) {
  const id = fieldId(idPrefix, "title");
  return (
    <div className={cn(className, "grid gap-2")}>
      <Label htmlFor={id}>Title</Label>
      {canWriteRegistrarFields ? (
        <Input id={id} value={form.title} onChange={(e) => setField("title", e.target.value)} {...commit} />
      ) : (
        <RegistrarFact id={id} value={form.title} />
      )}
    </div>
  );
}

export function CourseCatalogIdentityFields({
  idPrefix, form, setField, canWriteRegistrarFields,
}: FieldGroupProps) {
  const shared = { idPrefix, form, setField, canWriteRegistrarFields };
  return (
    <div className="grid grid-cols-2 gap-4">
      <CourseCodeField {...shared} />
      <CourseCreditsField {...shared} />
      <CourseTitleField {...shared} className="col-span-2" />
    </div>
  );
}

export function CourseModalityField({ idPrefix, form, setField, className }: FieldProps) {
  const id = fieldId(idPrefix, "modality");
  return (
    <div className={cn(className, "grid gap-2")}>
      <Label htmlFor={id}>Session format</Label>
      <Select value={form.modality} onValueChange={(v) => setField("modality", v as Modality)}>
        <SelectTrigger id={id} className="w-full"><SelectValue /></SelectTrigger>
        <SelectContent>
          {MODALITY_OPTIONS.map((m) => <SelectItem key={m} value={m}>{MODALITY_LABEL[m]}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

export function CourseMeetingProviderField({
  idPrefix, form, setField, className, enabledProviders,
}: FieldProps & { enabledProviders: MeetingProvider[] }) {
  const id = fieldId(idPrefix, "meeting-provider");
  return (
    <div className={cn(className, "grid gap-2")}>
      <Label htmlFor={id}>Meeting service</Label>
      <Select value={form.meetingProvider} onValueChange={(v) => setField("meetingProvider", v as MeetingProvider)}>
        <SelectTrigger id={id} className="w-full"><SelectValue /></SelectTrigger>
        <SelectContent>
          {enabledProviders.map((p) => <SelectItem key={p} value={p}>{PROVIDER_LABEL[p]}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

export function CourseMeetingDaysField({ form, setField, className }: Omit<FieldProps, "idPrefix">) {
  return (
    <div className={cn(className, "grid gap-2")}>
      <Label>Meeting days</Label>
      <div className="flex flex-wrap gap-3" role="group" aria-label="Meeting days">
        {WEEKDAY_OPTIONS.map(([day, label]) => (
          <label key={day} className="flex items-center gap-1.5 text-ui">
            <Checkbox checked={form.meetingWeekdays.includes(day)} onCheckedChange={(checked) => setField("meetingWeekdays", checked ? [...form.meetingWeekdays, day].sort() : form.meetingWeekdays.filter((item) => item !== day))} />
            {label}
          </label>
        ))}
      </div>
    </div>
  );
}

export function CourseMeetingStartField({ idPrefix, form, setField, className }: FieldProps) {
  const id = fieldId(idPrefix, "meeting-start");
  return (
    <div className={cn(className, "grid gap-2")}>
      <Label htmlFor={id}>Starts</Label>
      <Select value={form.meetingStartsAt || NONE} onValueChange={(value) => setField("meetingStartsAt", value === NONE ? "" : value)}>
        <SelectTrigger id={id} className="w-full"><SelectValue placeholder="Select start time" /></SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>Not scheduled</SelectItem>
          {TIME_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

export function CourseMeetingEndField({ idPrefix, form, setField, className }: FieldProps) {
  const id = fieldId(idPrefix, "meeting-end");
  return (
    <div className={cn(className, "grid gap-2")}>
      <Label htmlFor={id}>Ends</Label>
      <Select value={form.meetingEndsAt || NONE} onValueChange={(value) => setField("meetingEndsAt", value === NONE ? "" : value)}>
        <SelectTrigger id={id} className="w-full"><SelectValue placeholder="Select end time" /></SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>Not scheduled</SelectItem>
          {TIME_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

export function CourseTimezoneField({ idPrefix, form, setField, className }: FieldProps) {
  const id = fieldId(idPrefix, "timezone");
  return (
    <div className={cn(className, "grid gap-2")}>
      <Label htmlFor={id}>Time zone</Label>
      {/* A region, chosen — not typed. The free-text field this replaces
          is how 'EST' got into the column: a fixed offset that puts a
          6:00 PM session an hour out for half of every autumn term. A
          section already saved with a zone off this list keeps it. */}
      <Select value={form.timezone} onValueChange={(v) => setField("timezone", v)}>
        <SelectTrigger id={id} className="w-full"><SelectValue /></SelectTrigger>
        <SelectContent>
          {[
            ...COURSE_TIMEZONE_OPTIONS,
            ...(COURSE_TIMEZONE_OPTIONS.some((z) => z === form.timezone)
              ? []
              : [form.timezone]),
          ].map((zone) => (
            <SelectItem key={zone} value={zone}>
              {courseTimezoneOptionLabel(zone)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function CourseMeetingUrlField({ idPrefix, form, setField, className, commit }: TypedFieldProps) {
  const id = fieldId(idPrefix, "meeting-url");
  return (
    <div className={cn(className, "grid gap-2")}>
      <Label htmlFor={id}>Meeting link</Label>
      <Input
        id={id}
        type="url"
        value={form.meetingUrl}
        onChange={(e) => setField("meetingUrl", e.target.value)}
        placeholder="https://…"
        {...commit}
      />
    </div>
  );
}

export function CourseLocationField({
  idPrefix, form, setField, className, commit, locations,
}: TypedFieldProps & { locations: string[] }) {
  const id = fieldId(idPrefix, "location");
  return (
    <div className={cn(className, "grid gap-2")}>
      <Label htmlFor={id}>Location</Label>
      <Input
        id={id}
        list={`${idPrefix}-location-suggestions`}
        value={form.location}
        onChange={(e) => setField("location", e.target.value)}
        placeholder="Free text — e.g. Room 204"
        {...commit}
      />
      <datalist id={`${idPrefix}-location-suggestions`}>
        {locations.map((loc) => <option key={loc} value={loc} />)}
      </datalist>
    </div>
  );
}

export function CourseCapacityField({
  idPrefix, form, setField, canWriteRegistrarFields, className, commit,
}: TypedFieldProps & { canWriteRegistrarFields: boolean }) {
  const id = fieldId(idPrefix, "capacity");
  return (
    <div className={cn(className, "grid gap-2")}>
      <Label htmlFor={id}>Capacity</Label>
      {/* Registrar-owned despite sitting in the Delivery group: the
          enrollment ceiling is an institutional decision, not a
          scheduling one. */}
      {canWriteRegistrarFields ? (
        <Input
          id={id}
          type="number"
          min={0}
          value={form.capacity}
          onChange={(e) => setField("capacity", e.target.value)}
          {...commit}
        />
      ) : (
        <RegistrarFact id={id} value={form.capacity} />
      )}
    </div>
  );
}

export function CourseDeliveryFields({
  idPrefix, form, setField, canWriteRegistrarFields, enabledProviders, locations,
}: FieldGroupProps & { enabledProviders: MeetingProvider[]; locations: string[] }) {
  const shared = { idPrefix, form, setField };
  return (
    <div className="grid grid-cols-2 gap-4">
      <CourseModalityField {...shared} />
      <CourseMeetingProviderField {...shared} enabledProviders={enabledProviders} />
      <CourseMeetingDaysField form={form} setField={setField} className="col-span-2" />
      <CourseMeetingStartField {...shared} />
      <CourseMeetingEndField {...shared} />
      <CourseTimezoneField {...shared} />
      <CourseMeetingUrlField {...shared} />
      <CourseLocationField {...shared} locations={locations} />
      <CourseCapacityField {...shared} canWriteRegistrarFields={canWriteRegistrarFields} />
    </div>
  );
}

/** A value that follows its source only once the source stops changing. */
function useSettledValue<T>(value: T, delayMs: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return settled;
}

/**
 * The course image, chosen by link and previewed in the exact tile Home draws.
 *
 * A link rather than an upload: IMAM stores no file bytes (ADR 0009), so the
 * image lives wherever the instructor already hosts it. The preview waits for
 * typing to pause so a half-typed link is never fetched, and a link that does
 * not load is reported but not refused, because members fall back to the
 * generated signature either way.
 */
export function CourseImageFields({
  idPrefix, form, setField, commit, onRemove, inputAria,
}: Pick<FieldGroupProps, "idPrefix" | "form" | "setField"> & {
  commit?: Pick<CourseFieldCommit, "onBlur" | "onKeyDown">;
  /** After Remove clears the link, for an editor that saves it then. */
  onRemove?: () => void;
  /** The box field owns the name, refusal and disabled state (CASE-026). */
  inputAria?: { "aria-labelledby": string; "aria-invalid"?: true; "aria-describedby"?: string; disabled?: boolean };
}) {
  const id = fieldId(idPrefix, "image-url");
  const noteId = `${id}-note`;
  const problem = courseImageUrlProblem(form.imageUrl);
  const candidate = problem ? null : form.imageUrl.trim() || null;
  const preview = useSettledValue(candidate, 400);
  const [settled, setSettled] = useState<CourseImageSettled | null>(null);
  const failed = preview !== null && settled?.url === preview && settled.state === "failed";
  const note = problem
    ?? (failed
      ? "This image didn't load. Members will see the signature until it does."
      : candidate
        ? "Shown beside the course on Home, cropped to a square."
        : "Without an image, the course keeps its generated signature on Home.");
  return (
    <div className="flex items-start gap-4">
      <CourseMark code={form.code} imageUrl={preview} onImageSettled={setSettled} />
      <div className="grid min-w-0 flex-1 gap-2">
        <Label htmlFor={id}>Image link</Label>
        <div className="flex gap-2">
          <Input
            id={id}
            type="url"
            inputMode="url"
            value={form.imageUrl}
            onChange={(e) => setField("imageUrl", e.target.value)}
            placeholder="https://…"
            {...inputAria}
            aria-invalid={inputAria?.["aria-invalid"] || problem ? true : undefined}
            aria-describedby={[noteId, inputAria?.["aria-describedby"]].filter(Boolean).join(" ")}
            {...commit}
          />
          {form.imageUrl && (
            <Button
              type="button"
              variant="outline"
              disabled={inputAria?.disabled}
              onClick={() => {
                setField("imageUrl", "");
                onRemove?.();
              }}
            >
              Remove
            </Button>
          )}
        </div>
        <p
          id={noteId}
          aria-live="polite"
          className={cn(
            "text-meta",
            problem ? "text-destructive" : failed ? "text-status-warning-foreground" : "text-muted-foreground",
          )}
        >
          {note}
        </p>
      </div>
    </div>
  );
}

/** The two standing conditions a placement can carry, wherever it is edited. */
export function CoursePlacementNotices({
  form, semesters, placementLocked = false,
}: {
  form: CourseForm;
  semesters: Semester[];
  placementLocked?: boolean;
}) {
  const selectedTerm = semesters.find((s) => s.id === form.semesterId);
  return (
    <>
      {placementLocked && (
        <p className="text-meta text-muted-foreground">
          Published sections must retain their program, term, and instructor of record.
        </p>
      )}
      {isHistoricalTerm(selectedTerm?.status) && (
        <p className="text-meta text-muted-foreground">
          {selectedTerm?.name} is closed. This section will be recorded as history: it can be
          edited and archived, but it cannot be published or take enrolments.
        </p>
      )}
    </>
  );
}

/** The composed arrangement the section record's edit dialog renders. */
export function CourseFormFields({
  idPrefix, form, setField, programs, semesters, instructors, canWriteRegistrarFields,
  enabledProviders, locations, placementLocked = false,
}: {
  idPrefix: string;
  form: CourseForm;
  setField: SetCourseField;
  programs: Program[];
  semesters: Semester[];
  instructors: InstructorOption[];
  canWriteRegistrarFields: boolean;
  enabledProviders: MeetingProvider[];
  locations: string[];
  placementLocked?: boolean;
}) {
  const shared = { idPrefix, form, setField, canWriteRegistrarFields, placementLocked };
  return (
    <div className="grid gap-5">
      {!canWriteRegistrarFields && (
        <p className="text-meta text-muted-foreground">
          Catalog identity, academic placement, and capacity are the registrar's record and are
          shown here for reference. You can edit how your section runs: its delivery, schedule,
          meeting destination, and location, and its image.
        </p>
      )}
      <fieldset className="grid gap-4">
        <legend className="eyebrow mb-2">Catalog identity</legend>
        <CourseCatalogIdentityFields {...shared} />
      </fieldset>

      <fieldset className="grid gap-4">
        <legend className="eyebrow mb-2">Academic placement</legend>
        <div className="grid grid-cols-2 gap-4">
          <CourseProgramField {...shared} programs={programs} />
          <CourseTermField {...shared} semesters={semesters} />
          <div className="col-span-2">
            <CourseInstructorField {...shared} instructors={instructors} />
          </div>
        </div>
        <CoursePlacementNotices form={form} semesters={semesters} placementLocked={placementLocked} />
      </fieldset>

      <fieldset className="grid gap-4">
        <legend className="eyebrow mb-2">Delivery</legend>
        <CourseDeliveryFields {...shared} enabledProviders={enabledProviders} locations={locations} />
      </fieldset>

      <fieldset className="grid gap-4">
        <legend className="eyebrow mb-2">Course image</legend>
        <CourseImageFields idPrefix={idPrefix} form={form} setField={setField} />
      </fieldset>
    </div>
  );
}
